<template>
  <div
    class="tla-number-input"
    :class="{
      'tla-number-input--no-controls': !controls,
      'tla-number-input--slider': slider
    }">
    <button
      v-if="controls"
      class="tla-number-input__button tla-number-input__button--minus"
      :class="moneyValidationClass"
      type="button"
      :disabled="!decreasable()"
      @click="decrease"
    />

    <money
      v-model="currentValue"
      v-bind="inputConfiguration"
      :class="[moneyValidationClass, { 'tla-number-input__input--no-value': currentValue === min }]"
      class="tla-form-group__input tla-form-group__input--large tla-number-input__input"
      :name="name"
      :min="min"
      :max="max"
      :step="step"
      :disabled="(!decreasable() && !increasable())"
      :placeholder="placeholder"
    ></money>

    <button
      v-if="controls"
      class="tla-number-input__button tla-number-input__button--plus"
      :class="moneyValidationClass"
      type="button"
      :disabled="!increasable()"
      @click="increase"
    />
  </div>
</template>

<script lang="ts">
import {
  Component, Vue, Prop, Watch
} from 'vue-property-decorator';
import money from 'v-money';

const { isNaN } = Number;
const REGEXP_DECIMALS = /\.\d*(?:0|9){10}\d*$/;
const normalizeDecimalNumber = (value, times = 100000000000) => (
  REGEXP_DECIMALS.test(value) ? (Math.round(value * times) / times) : value
);

Vue.use(money, { precision: 4 });

@Component
export default class NumberInput extends Vue {
  @Prop(Number)
  public value!: number;

  @Prop({ default: 1 })
  public step!: number;

  @Prop({ default: Infinity })
  public max!: number;

  @Prop({ default: -Infinity })
  public min!: number;

  @Prop({ default: true })
  public controls!: boolean;

  @Prop({ default: false })
  public slider!: boolean;

  @Prop(String)
  public name!: string;

  @Prop(String)
  public placeholder!: string;

  @Prop({ default: 2 })
  public precision!: number;

  @Prop({ default: '£' })
  public prefix!: string;

  @Prop({ default: '' })
  public suffix!: string;

  @Prop({ default: '' })
  public fieldName!: string;

  @Prop({ default: '' })
  public moneyValidationClass!: string;

  public currentValue: number = 0;

  public inputConfiguration : any = null;

  public mounted() {
    this.inputConfiguration = {
      decimal: '.',
      thousands: ',',
      prefix: this.prefix,
      suffix: this.suffix,
      precision: this.precision,
      masked: false
    };
  }

  /**
   * Indicate if the value is increasable.
   * @returns {boolean} Return `true` if it is decreasable, else `false`.
   */
  public increasable(): boolean {
    const num = this.currentValue;

    return isNaN(num) || num < this.max;
  }

  /**
   * Indicate if the value is decreasable.
   * @returns {boolean} Return `true` if it is decreasable, else `false`.
   */
  public decreasable(): boolean {
    const num = this.currentValue;

    return isNaN(num) || num > this.min;
  }

  @Watch('value', { immediate: true })
  public onValueChanged(newValue: number, oldValue: number) {
    if (
      // Avoid to trigger change event when created
      !(isNaN(newValue) && typeof oldValue === 'undefined')

      // Avoid infinite loop
      && newValue !== this.currentValue
    ) {
      this.setValue(newValue);
    }
  }

  /**
   * Change event handler.
   * @param {string} value - The new value.
   */
  public change(event) {
    this.setValue(Math.min(this.max, Math.max(this.min, event.target.value)));
  }

  /**
   * Decrease the value.
   */
  public decrease() {
    if (this.decreasable()) {
      let { currentValue } = this;

      if (isNaN(currentValue)) {
        currentValue = 0;
      }

      this.setValue(Math.min(this.max, Math.max(
        this.min,
        normalizeDecimalNumber(currentValue - this.step)
      )));
    }
  }

  /**
   * Increase the value.
   */
  public increase() {
    if (this.increasable()) {
      let { currentValue } = this;

      if (isNaN(currentValue)) {
        currentValue = 0;
      }

      this.setValue(Math.min(this.max, Math.max(
        this.min,
        normalizeDecimalNumber(currentValue + this.step)
      )));
    }
  }

  /**
   * Set new value and dispatch change event.
   * @param {number} value - The new value to set.
   */
  public setValue(value) {
    const oldValue = this.currentValue;
    let newValue = value;

    if (this.min <= this.max) {
      newValue = Math.min(this.max, Math.max(this.min, newValue));
    }

    this.currentValue = newValue;
  }

  @Watch('currentValue')
  public updateValue() {
    this.$emit(this.fieldName, this.currentValue);
  }
}
</script>
